import React from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@stitches/react';
import AnimatedSection from '../../components/common/AnimatedSection';

// 页面样式组件
const TermsContainer = styled('div', {
  padding: '4rem 2rem',
  maxWidth: '1000px',
  margin: '0 auto',
});

const TermsHeader = styled('div', {
  textAlign: 'center',
  marginBottom: '3rem',
});

const TermsTitle = styled('h1', {
  fontSize: '2.5rem',
  marginBottom: '1rem',
  fontWeight: 700,
});

const TermsLastUpdated = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  fontStyle: 'italic',
});

const TermsSection = styled('section', {
  marginBottom: '2.5rem',
});

const SectionTitle = styled('h2', {
  fontSize: '1.8rem',
  marginBottom: '1.5rem',
  paddingBottom: '0.5rem',
  borderBottom: '2px solid $borderLight',
  fontWeight: 600,
});

const SectionContent = styled('div', {
  fontSize: '1.1rem',
  lineHeight: 1.7,
  color: '$textPrimary',
  
  p: {
    marginBottom: '1rem',
  },
  
  ul: {
    paddingLeft: '1.5rem',
    marginBottom: '1rem',
  },
  
  li: {
    marginBottom: '0.5rem',
  },
  
  a: {
    color: '$primary',
    textDecoration: 'none',
    
    '&:hover': {
      textDecoration: 'underline',
    }
  }
});

const SubSectionTitle = styled('h3', {
  fontSize: '1.4rem',
  marginTop: '2rem',
  marginBottom: '1rem',
  fontWeight: 500,
});

/**
 * 服务条款页面组件
 * 展示平台的服务条款、用户协议和法律要求
 */
const TermsOfService: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <TermsContainer>
      <AnimatedSection>
        <TermsHeader>
          <TermsTitle>{t('terms.title')}</TermsTitle>
          <TermsLastUpdated>{t('terms.lastUpdated', { date: '2023-04-01' })}</TermsLastUpdated>
        </TermsHeader>
      </AnimatedSection>
      
      <AnimatedSection delay={0.1}>
        <TermsSection>
          <SectionTitle>{t('terms.introduction.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.introduction.paragraph1')}</p>
            <p>{t('terms.introduction.paragraph2')}</p>
            <p>{t('terms.introduction.paragraph3')}</p>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.2}>
        <TermsSection>
          <SectionTitle>{t('terms.definitions.title')}</SectionTitle>
          <SectionContent>
            <ul>
              <li><strong>{t('terms.definitions.service.term')}:</strong> {t('terms.definitions.service.definition')}</li>
              <li><strong>{t('terms.definitions.user.term')}:</strong> {t('terms.definitions.user.definition')}</li>
              <li><strong>{t('terms.definitions.miningContract.term')}:</strong> {t('terms.definitions.miningContract.definition')}</li>
              <li><strong>{t('terms.definitions.wallet.term')}:</strong> {t('terms.definitions.wallet.definition')}</li>
              <li><strong>{t('terms.definitions.cryptocurrency.term')}:</strong> {t('terms.definitions.cryptocurrency.definition')}</li>
            </ul>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.3}>
        <TermsSection>
          <SectionTitle>{t('terms.accountRegistration.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.accountRegistration.paragraph1')}</p>
            <p>{t('terms.accountRegistration.paragraph2')}</p>
            <ul>
              <li>{t('terms.accountRegistration.item1')}</li>
              <li>{t('terms.accountRegistration.item2')}</li>
              <li>{t('terms.accountRegistration.item3')}</li>
              <li>{t('terms.accountRegistration.item4')}</li>
            </ul>
            <p>{t('terms.accountRegistration.paragraph3')}</p>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.4}>
        <TermsSection>
          <SectionTitle>{t('terms.services.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.services.paragraph1')}</p>
            <SubSectionTitle>{t('terms.services.miningServices.title')}</SubSectionTitle>
            <p>{t('terms.services.miningServices.description')}</p>
            <SubSectionTitle>{t('terms.services.hostingServices.title')}</SubSectionTitle>
            <p>{t('terms.services.hostingServices.description')}</p>
            <SubSectionTitle>{t('terms.services.nftServices.title')}</SubSectionTitle>
            <p>{t('terms.services.nftServices.description')}</p>
            <SubSectionTitle>{t('terms.services.walletServices.title')}</SubSectionTitle>
            <p>{t('terms.services.walletServices.description')}</p>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.5}>
        <TermsSection>
          <SectionTitle>{t('terms.payments.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.payments.paragraph1')}</p>
            <p>{t('terms.payments.paragraph2')}</p>
            <ul>
              <li>{t('terms.payments.item1')}</li>
              <li>{t('terms.payments.item2')}</li>
              <li>{t('terms.payments.item3')}</li>
            </ul>
            <p>{t('terms.payments.paragraph3')}</p>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.6}>
        <TermsSection>
          <SectionTitle>{t('terms.userResponsibilities.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.userResponsibilities.paragraph1')}</p>
            <ul>
              <li>{t('terms.userResponsibilities.item1')}</li>
              <li>{t('terms.userResponsibilities.item2')}</li>
              <li>{t('terms.userResponsibilities.item3')}</li>
              <li>{t('terms.userResponsibilities.item4')}</li>
              <li>{t('terms.userResponsibilities.item5')}</li>
            </ul>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.7}>
        <TermsSection>
          <SectionTitle>{t('terms.intellectualProperty.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.intellectualProperty.paragraph1')}</p>
            <p>{t('terms.intellectualProperty.paragraph2')}</p>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.8}>
        <TermsSection>
          <SectionTitle>{t('terms.disclaimer.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.disclaimer.paragraph1')}</p>
            <p>{t('terms.disclaimer.paragraph2')}</p>
            <p>{t('terms.disclaimer.paragraph3')}</p>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={0.9}>
        <TermsSection>
          <SectionTitle>{t('terms.limitation.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.limitation.paragraph1')}</p>
            <p>{t('terms.limitation.paragraph2')}</p>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={1.0}>
        <TermsSection>
          <SectionTitle>{t('terms.termination.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.termination.paragraph1')}</p>
            <p>{t('terms.termination.paragraph2')}</p>
            <ul>
              <li>{t('terms.termination.item1')}</li>
              <li>{t('terms.termination.item2')}</li>
              <li>{t('terms.termination.item3')}</li>
            </ul>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={1.1}>
        <TermsSection>
          <SectionTitle>{t('terms.governingLaw.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.governingLaw.paragraph1')}</p>
            <p>{t('terms.governingLaw.paragraph2')}</p>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={1.2}>
        <TermsSection>
          <SectionTitle>{t('terms.changes.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.changes.paragraph1')}</p>
            <p>{t('terms.changes.paragraph2')}</p>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
      
      <AnimatedSection delay={1.3}>
        <TermsSection>
          <SectionTitle>{t('terms.contact.title')}</SectionTitle>
          <SectionContent>
            <p>{t('terms.contact.paragraph1')}</p>
            <p>{t('terms.contact.paragraph2', { email: 'terms@BITENGINE.com' })}</p>
          </SectionContent>
        </TermsSection>
      </AnimatedSection>
    </TermsContainer>
  );
};

export default TermsOfService; 